<template>
    <div v-if="show" class="nevs-popup-container">
        <div class="nevs-popup send-email-popup">
            <div class="nevs-popup-text">
                <NevsTextField v-model="data.to" :error="validation.to"
                               :label="$LANG.Get('fields.emailTo') + ':'"></NevsTextField>
                <NevsTextField v-model="data.cc" :label="$LANG.Get('fields.emailCc') + ':'"></NevsTextField>
                <NevsTextField v-model="data.bcc" :label="$LANG.Get('fields.emailBcc') + ':'"></NevsTextField>
                <div class="send-email-disclaimer">{{ $LANG.Get('labels.emailAddressSeparatorNote') }}</div>
                <div class="attachment-section">
                    <div v-for="(attachment, index) in data.attachments" :key="index" class="attachment-row">
                        <NevsUpload v-model="data.attachments[index]" width="auto"></NevsUpload>
                        <span class="attachment-name" :title="attachment.name">{{ attachment.name }}</span>
                        <span class="attachment-remove" @click="removeAttachment(index)"><i class="fa-solid fa-trash"></i></span>
                    </div>
                    <NevsButton @click="addAttachment()">
                        <i class="fa-solid fa-paperclip"></i>
                        {{ $LANG.Get('buttons.addAttachment') }}
                    </NevsButton>
                </div>
                <div class="send-email-disclaimer">
                    <i class="fa-solid fa-circle-info"></i>
                    {{ $LANG.Get('labels.documentAttachedNote') }}
                </div>
            </div>
            <div class="nevs-popup-buttons">
                <NevsButton class="error" @click="cancelClick()"><i class="fa-solid fa-xmark"></i> {{ $LANG.Get('buttons.cancel') }}</NevsButton>
                <NevsButton class="success" @click="sendClick()"><i class="fa-solid fa-paper-plane"></i> {{ $LANG.Get('buttons.send') }}</NevsButton>
            </div>
        </div>
    </div>
</template>

<script>
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsUpload from "@/components/nevs/NevsUpload.vue";

export default {
    name: "ModalSendEmail",
    components: {NevsUpload, NevsTextField, NevsButton},
    props: {
        show: {
            type: Boolean,
            default: false
        },
        documentId: {
            type: Number,
            default: null
        },
        endpoint: {
            type: String,
            default: ''
        },
        // The customer's official addresses, as the semicolon-separated string the list endpoint returns.
        // Pre-fills the recipient field; the sender is free to change it before sending.
        to: {
            type: String,
            default: ''
        }
    },
    emits: ['done'],
    data() {
        return {
            data: {
                to: '',
                cc: '',
                bcc: '',
                attachments: []
            },
            validation: {
                to: ''
            }
        }
    },
    watch: {
        // The subject and the body are the server's to decide (fixed wording), so the dialog asks only for
        // recipients, cc / bcc and extra attachments. Recipients start at the customer's official addresses.
        show() {
            this.data.to = this.to ?? '';
            this.data.cc = this.$store.state.user.client.email_copy ?? '';
            this.data.bcc = '';
            this.data.attachments = [];
            this.validation.to = '';
        }
    },
    methods: {
        addAttachment() {
            this.data.attachments.push({id: 0, name: '', link: ''});
        },
        removeAttachment(index) {
            this.data.attachments.splice(index, 1);
        },
        cancelClick() {
            this.$emit('done', false);
        },
        sendClick() {
            let vm = this;
            let toList = this.data.to.split(';').map(e => e.trim()).filter(e => e !== '');
            let ccList = this.data.cc.split(';').map(e => e.trim()).filter(e => e !== '');
            let bccList = this.data.bcc.split(';').map(e => e.trim()).filter(e => e !== '');
            let attachmentIds = this.data.attachments.filter(a => a.id > 0).map(a => a.id);

            // Nothing to send to: the server refuses this too, but the sender should hear it from the dialog
            // they are looking at rather than as a failed request.
            if (toList.length === 0) {
                this.validation.to = this.$LANG.Get('alerts.fieldMissing');
                return;
            }
            this.validation.to = '';

            this.$API.APICall('post', this.endpoint + '/email/' + this.documentId, {
                to: toList,
                cc: ccList,
                bcc: bccList,
                attachment_ids: attachmentIds
            }, (data, success) => {
                if (success) {
                    vm.$LOCAL_BUS.TriggerEvent('popup', {text: vm.$LANG.Get('alerts.emailSent'), type: 'alert'});
                    vm.$emit('done', true);
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.send-email-popup {
    width: 500px;
}

.attachment-section {
    margin-top: 8px;
}

.attachment-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

/* Takes the space the upload control leaves; a long file name is cut rather than pushing the remove
   button out of the row (the full name stays available as the title). */
.attachment-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.9em;
}

.attachment-remove {
    cursor: pointer;
    color: $danger;
    padding: 4px;
}

.send-email-disclaimer {
    margin-top: 10px;
    font-size: 0.85em;
    opacity: 0.7;
}
</style>
